import React, { FC, PropsWithChildren, useEffect, useState } from 'react'
import format from 'date-fns/format'
import { Sensor, SensorState } from 'src/api'
import { SensorsListResult } from 'src/interfaces'
import { TabGroupFilter, PropsInjector, DataLoad } from 'src/components'
import { useSensorListRequest, useSensorMapViewListRequest } from 'src/hooks/api'
import { useQueryParamsState, useDialog, useAddPopupMessage } from 'src/hooks/ui'
import { transformSensorsState, transformBoolean as transformKeyAndValue, transformAgeOfReadings, transformSensorCurrentConfiguration } from 'src/hooks/api/helpers'
import { SensorStateList, SearchParamsKeys, ViewTypes, DialogNames } from 'src/enums'
import { parseSearchIdentifiers, SEARCH_IDENTIFIERS_CAP } from 'src/helpers'
import {
  AssignedSensorList,
  AvailableSensorList,
  InUseSensorList,
  NewSensorList,
  AllSensorList,
  ThingspaceSensorsList,
  SensorDiagnosticsList,
  VerizonReportsList,
  LostSensorList,
  UnusedSensorList,
  NotFoundSensorsDialog
} from './sensorList'

const getStatusList = ({
  data,
  count,
  isLoading,
  states,
  refetchSensorList
}: {
  data: Sensor[]
  count: number
  isLoading: boolean
  states: SensorState[]
  refetchSensorList: () => void
}) => [
  {
    status: SensorStateList.All,
    content: <AllSensorList data={data} count={count} isLoading={isLoading} states={states} refetchSensorList={refetchSensorList} />
  },
  {
    status: SensorStateList.New,
    content: <NewSensorList data={data} count={count} isLoading={isLoading} states={states} refetchSensorList={refetchSensorList} />
  },
  {
    status: SensorStateList.Available,
    content: <AvailableSensorList data={data} count={count} isLoading={isLoading} states={states} refetchSensorList={refetchSensorList} />
  },
  {
    status: SensorStateList.Assigned,
    content: <AssignedSensorList data={data} count={count} isLoading={isLoading} states={states} refetchSensorList={refetchSensorList} />
  },
  {
    status: SensorStateList.InUse,
    content: <InUseSensorList data={data} count={count} isLoading={isLoading} states={states} refetchSensorList={refetchSensorList} />
  },
  {
    status: SensorStateList.Lost,
    content: <LostSensorList data={data} count={count} isLoading={isLoading} states={states} refetchSensorList={refetchSensorList} />
  },
  {
    status: SensorStateList.Thingspace,
    content: <ThingspaceSensorsList />
  },
  {
    status: SensorStateList.Diagnostics,
    content: <SensorDiagnosticsList data={data} count={count} isLoading={isLoading} refetchSensorList={refetchSensorList} />
  },
  {
    status: SensorStateList.VerizonReports,
    content: <VerizonReportsList />
  },
  {
    status: SensorStateList.UnusedSensor,
    content: <UnusedSensorList data={data} count={count} isLoading={isLoading} />
  }
]

interface SensorTabsProps {
  data?: Sensor[]
  count?: number
  isLoading?: boolean
  refetchSensorList?: () => void
  states?: SensorState[]
}

const SensorTabs: FC<SensorTabsProps> = ({ data = [], count = 0, isLoading = false, refetchSensorList = () => {}, states = [] }) => {
  return (
    <TabGroupFilter
      statusList={getStatusList({
        data,
        count,
        isLoading,
        refetchSensorList,
        states
      })}
      name={SearchParamsKeys.State}
      paramsToKeep={[SearchParamsKeys.State, SearchParamsKeys.ViewType, SearchParamsKeys.Search]}
    />
  )
}

interface SensorListLoaderProps {
  states: SensorState[]
  typeId?: number
}

const SensorListLoader: FC<PropsWithChildren<SensorListLoaderProps>> = ({ states, typeId, children }) => {
  const state = useQueryParamsState()
  const viewType = state[SearchParamsKeys.ViewType] || ViewTypes.List

  const useRequest = viewType === ViewTypes.List ? useSensorListRequest : useSensorMapViewListRequest

  const { data, error, isInitialLoading, isError, refetch, dataUpdatedAt } = useRequest({
    params: {
      ...state,
      ...(typeId ? { typeId } : {}),
      ...(viewType === ViewTypes.Map
        ? {
            limit: 10000,
            offset: 0
          }
        : null)
    },
    paramsTransform: [
      transformSensorsState(states || []),
      transformKeyAndValue(SearchParamsKeys.LowBattery, SearchParamsKeys.BatteryLevel, 25),
      transformKeyAndValue(SearchParamsKeys.WeakSignal, SearchParamsKeys.SignalStrength, -100),
      transformKeyAndValue(
        SearchParamsKeys.LastPingMoreThanHour,
        SearchParamsKeys.LastPing,
        JSON.stringify({
          lt: format(new Date(new Date().setHours(new Date().getHours() - 1)), "yyyy-MM-dd'T'HH:mm:ssxxx")
        })
      ),
      transformKeyAndValue(SearchParamsKeys.SyncPeriodLessThanHour, SearchParamsKeys.SyncPeriod, JSON.stringify({ lt: 3600 })),
      transformAgeOfReadings(state[SearchParamsKeys.AgeOfReadingsMoreThanHour] || ''),
      transformSensorCurrentConfiguration(state[SearchParamsKeys.CurrentSensorConfiguration] || '')
    ],
    options: {
      ...(state.state === SensorStateList.Thingspace || state.state === SensorStateList.VerizonReports
        ? {
            enabled: false
          }
        : {})
    }
  })

  const { open: notFoundDialogOpen, openDialog: openNotFoundDialog, closeDialog: closeNotFoundDialog } = useDialog(DialogNames.SensorsNotFound)
  const addPopupMessage = useAddPopupMessage()
  const [notFoundResult, setNotFoundResult] = useState<{ identifiers: string[]; total: number }>({ identifiers: [], total: 0 })

  // Open the "not found" dialog whenever a search response comes back with unmatched
  // identifiers. Keyed on `dataUpdatedAt` so it fires once per resolved fetch (closing
  // the dialog does not re-open it; re-running a search does).
  useEffect(() => {
    const notFound = (data as SensorsListResult | undefined)?.notFound
    if (!notFound || notFound.length === 0) return
    const { identifiers, truncated } = parseSearchIdentifiers(state[SearchParamsKeys.Search])
    setNotFoundResult({ identifiers: notFound, total: identifiers.length })
    openNotFoundDialog()
    if (truncated) {
      addPopupMessage({ text: `Only the first ${SEARCH_IDENTIFIERS_CAP} unique identifiers were searched.`, type: 'info' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt])

  const props = {
    data: data?.rows || [],
    count: data?.count || 0,
    isLoading: isInitialLoading,
    states,
    refetchSensorList: refetch
  }

  return (
    <>
      <DataLoad isLoading={false} isError={isError} errorMessage={error?.message}>
        <PropsInjector props={props}>{children}</PropsInjector>
      </DataLoad>
      <NotFoundSensorsDialog open={notFoundDialogOpen} onClose={closeNotFoundDialog} identifiers={notFoundResult.identifiers} total={notFoundResult.total} />
    </>
  )
}

interface SensorListProps {
  states?: SensorState[]
  typeId?: number
}

export const SensorList: FC<SensorListProps> = ({ states = [], typeId }) => {
  return (
    <SensorListLoader states={states} typeId={typeId}>
      <SensorTabs />
    </SensorListLoader>
  )
}
